/**
 * Scribe LLM Service
 *
 * Parses free-form text (typed or voice transcript) into one or more
 * structured item proposals. Each proposal includes:
 *   - title, description, category, altitude (1–5)
 *   - confidence, rationale
 *   - relationship hints (item names mentioned in the text)
 *
 * Runs through Peak LLM only — honors the Scribe agent's model and model
 * parameters; the workspace prompt is an overlay on the contract
 * (supabase/functions/_shared/draftItemsCore.ts), never a replacement.
 * Throws {@link AgentLlmError} when Peak LLM is unavailable or misconfigured.
 */

import type { ItemLanguageType } from '../types/prioritization/lifecycle';
import { AgentLlmError, callAgentLlm } from './llmClient';
import { buildDraftItemsSystemPrompt, DRAFT_ITEMS_JOB } from '../../supabase/functions/_shared/draftItemsCore.ts';
import { resolveModelParams } from '../../supabase/functions/_shared/estimateValueCore.ts';
import { fenceReferenceDocs } from '../../supabase/functions/_shared/referenceDocFence.ts';
import type { AgentReferenceDoc } from './agentSettingsService';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ScribeParseInput {
  text: string;
  model?: string;
  /** The workspace's prompt overlay — it specializes the contract, never replaces it. */
  systemPrompt?: string | null;
  modelParams?: Record<string, unknown> | null;
  referenceDocs?: AgentReferenceDoc[];
}

export interface ScribeProposedItem {
  /** Temporary local ID for review state — not persisted */
  _localId: string;
  title: string;
  description: string;
  category: ItemLanguageType;
  altitude: number;
  confidence: 'high' | 'medium' | 'low';
  rationale: string;
  /** Titles/names mentioned in the text that the LLM thinks are related items */
  relationshipHints: string[];
}

// ── Category definitions ───────────────────────────────────────────────────────

const ALL_CATEGORIES: ItemLanguageType[] = [
  'problem', 'opportunity', 'risk', 'solution', 'commitment', 'investment',
];

const CATEGORY_DEFINITIONS: Record<ItemLanguageType, string> = {
  problem:    'Something is wrong that needs fixing — a pain, gap, failure, or degradation.',
  opportunity:'Something we could gain by taking action — upside potential if we act.',
  risk:       'Something that might go wrong if left unaddressed — a threat or exposure.',
  solution:   'A specific approach, initiative, or implementation we could execute.',
  commitment: 'An external promise we must keep — contractual, regulatory, or to a stakeholder.',
  investment: 'A strategic bet for future value — capability building, platform, or R&D.',
};

// ── Altitude guidance ─────────────────────────────────────────────────────────

const ALTITUDE_GUIDE = `Altitude 1–5 is determined by three dimensions (each scored 1–5, averaged):
- timeHorizon: 1=days/weeks, 3=1–3 months, 5=1+ year
- workSize:    1=hours/days, 3=weeks/sprint, 5=multi-quarter program
- impactSurface: 1=one person/team, 3=department, 5=whole org or external
Example: "Open an office in Paris" → timeHorizon=5, workSize=5, impactSurface=5 → altitude 5
Example: "Fix login button color" → timeHorizon=1, workSize=1, impactSurface=1 → altitude 1`;

// ── Prompt builder ────────────────────────────────────────────────────────────

// Reference documents an administrator pasted in, fenced as reference material
// by the same helper every other prompt builder uses (cleaned and capped there too).
function refSection(docs: AgentReferenceDoc[] = []): string {
  const fenced = fenceReferenceDocs(docs, 'how this workspace names and sizes work');
  return fenced ? `${fenced}\n\n` : '';
}

/** The user prompt Scribe sends. Exported for its tests. */
export function buildScribePrompt(input: ScribeParseInput): string {
  const categorySection = ALL_CATEGORIES
    .map((c) => `- ${c}: ${CATEGORY_DEFINITIONS[c]}`)
    .join('\n');

  return `You are Scribe, a strategic intake agent for a prioritization system.

Your job is to read a block of free-form text and extract every distinct work item, idea, problem, or initiative mentioned. For each one, produce a structured item proposal.

## Item Categories
${categorySection}

## Altitude Scoring (1–5)
${ALTITUDE_GUIDE}

${refSection(input.referenceDocs)}## Input Text
"""
${input.text.trim()}
"""

## Output Format
Respond with valid JSON only — no markdown, no extra text:
{
  "items": [
    {
      "title": "<short, actionable title>",
      "description": "<1–3 sentences expanding on the item using context from the text>",
      "category": "<one of: problem|opportunity|risk|solution|commitment|investment>",
      "altitude": <1–5>,
      "confidence": "<high|medium|low>",
      "rationale": "<1–2 sentences explaining category and altitude choice>",
      "relationshipHints": ["<name of related item or initiative mentioned in the text>"]
    }
  ]
}

Rules:
- Extract EVERY distinct item mentioned, even if brief.
- If the text mentions only one item, return an array with one element.
- If the text is not about work items at all, return { "items": [] }.
- Do not hallucinate items not mentioned in the text.
- Keep titles short (≤10 words).
- relationshipHints should only include names explicitly mentioned in the text.`;
}

// ── Response parser ───────────────────────────────────────────────────────────

function localId(): string {
  return 'scribe-' + Math.random().toString(36).slice(2, 10);
}

function parseScribeResponse(content: string): ScribeProposedItem[] {
  const jsonMatch = content.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new AgentLlmError('Scribe agent could not parse the LLM response. Check Peak LLM logs.');
  }

  try {
    const parsed = JSON.parse(jsonMatch[0]);
    const rawItems = Array.isArray(parsed.items) ? parsed.items : [];

    return rawItems
      .filter(
        (item: unknown) =>
          item &&
          typeof item === 'object' &&
          typeof (item as Record<string, unknown>).title === 'string' &&
          (item as Record<string, unknown>).title,
      )
      .map((item: Record<string, unknown>): ScribeProposedItem => {
        const rawCategory = item.category as string;
        const category: ItemLanguageType = ALL_CATEGORIES.includes(rawCategory as ItemLanguageType)
          ? (rawCategory as ItemLanguageType)
          : 'problem';

        const rawAltitude = Number(item.altitude);
        const altitude = isNaN(rawAltitude) ? 3 : Math.max(1, Math.min(5, Math.round(rawAltitude)));

        const confidence: ScribeProposedItem['confidence'] =
          item.confidence === 'high' || item.confidence === 'low' ? item.confidence : 'medium';

        const hints = Array.isArray(item.relationshipHints)
          ? (item.relationshipHints as unknown[]).filter((h) => typeof h === 'string') as string[]
          : [];

        return {
          _localId: localId(),
          title: String(item.title).trim(),
          description: typeof item.description === 'string' ? item.description.trim() : '',
          category,
          altitude,
          confidence,
          rationale: typeof item.rationale === 'string' ? item.rationale.trim() : '',
          relationshipHints: hints,
        };
      });
  } catch {
    throw new AgentLlmError('Scribe agent could not parse the LLM response. Check Peak LLM logs.');
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Parse free-form text into an array of item proposals.
 * Returns [] when the text is empty or the LLM finds no items.
 * Throws {@link AgentLlmError} when Peak LLM fails or the response is unparseable.
 */
export async function parseTextToItems(input: ScribeParseInput): Promise<ScribeProposedItem[]> {
  if (!input.text.trim()) return [];

  const model = input.model ?? DEFAULT_MODEL;
  const params = resolveModelParams(DRAFT_ITEMS_JOB.modelDefaults, input.modelParams ?? null);
  const { systemPrompt } = buildDraftItemsSystemPrompt({ overlay: input.systemPrompt ?? null, language: params.language });
  const userPrompt = buildScribePrompt(input);

  const content = await callAgentLlm({
    userPrompt,
    systemPrompt,
    model,
    conversationId: `scribe-${Date.now()}`,
    maxTokens: params.maxTokens,
    temperature: params.temperature,
  });

  return parseScribeResponse(content);
}
